import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSymbolData } from "../dataSource";
import "./IdeasPanel.css";

/**
 * Strategy ideas backtested on this symbol's daily sentiment score.
 *
 * Results are in R-multiples, not currency: 1R is the risk the trade took on
 * (its initial ATR stop distance). Raw price PnL is not comparable across
 * symbols — BITCOIN at ~100k would swamp SILVER at ~30 in any aggregate.
 *
 * "vs random" is the percentile of this idea's return within 50 random-entry
 * controls matched to its own trade frequency and using its own exit. 50 means
 * indistinguishable from entering at random. It is shown next to every return
 * on purpose: with a handful of trades per idea the return alone is noise.
 */

function fmtR(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `${n > 0 ? "+" : ""}${n.toFixed(2)}R` : "—";
}

/** Trade list, tolerant of older exports where `trades` was the trade COUNT
 *  rather than the per-trade records. Calling .filter on that number throws
 *  and takes the whole page down with it. */
function tradeList(idea) {
  return Array.isArray(idea?.trades) ? idea.trades : [];
}

/** Trade count, from either the current `trade_count` field or the legacy
 *  numeric `trades`. */
function tradeCount(idea) {
  if (Number.isFinite(idea?.trade_count)) return idea.trade_count;
  if (Number.isFinite(idea?.trades)) return idea.trades;
  return tradeList(idea).length;
}

function ordinal(v) {
  const n = Math.round(v);
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
}

export default function IdeasPanel({ symbol, selected, onSelect, chartDates }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    let live = true;
    setData(null);
    setFailed(false);
    fetchSymbolData("ideas", symbol)
      .then(d => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [symbol]);

  // Clear a selection that belongs to a different symbol's dataset.
  useEffect(() => { onSelect && onSelect(null); }, [symbol]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (failed || !data || !data.ideas?.length) return null;

  // The chart shows ~35 candles but backtests run over the full score history,
  // so a strategy's earliest trades often have no x-axis slot. Count them so the
  // panel can say why fewer markers appear than the trade count.
  const firstChartDate = chartDates?.length ? chartDates[0] : null;
  const offChart = t => firstChartDate != null && t.open_date < firstChartDate;

  const s = data.summary;

  return (
    <div className="ip-panel">
      <div className="ip-title">{t("ideas.title")}</div>

      <p className="ip-sub">
        {t("ideas.sub", { tested: s.strategies_tested, bars: data.bars, median: s.median_trades })}
      </p>
      <p className="ip-hint">
        {selected
          ? t("ideas.showing", { name: selected.name })
          : t("ideas.clickHint")}
      </p>

      <div className="ip-scroll">
        <table className="ip-table">
          <thead>
            <tr>
              <th className="ip-l">{t("ideas.colIdea")}</th>
              <th className="ip-l">{t("ideas.colType")}</th>
              <th>{t("ideas.colTrades")}</th>
              <th>{t("ideas.colTotal")}</th>
              <th>{t("ideas.colPerTrade")}</th>
              <th>{t("ideas.colWin")}</th>
              <th>{t("ideas.colVsRandom")}</th>
            </tr>
          </thead>
          <tbody>
            {data.ideas.map(i => {
              const isSel = selected?.code === i.code && selected?.name === i.name;
              const hidden = tradeList(i).filter(offChart).length;
              const neg = i.R <= 0;
              return (
                <tr
                  key={i.code + i.name}
                  className={`ip-row${neg ? " ip-neg" : ""}${isSel ? " ip-selected" : ""}`}
                  tabIndex={0}
                  onClick={() => onSelect && onSelect(isSel ? null : { ...i, trades: tradeList(i) })}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect && onSelect(isSel ? null : { ...i, trades: tradeList(i) });
                    }
                  }}
                >
                  <td className="ip-l">
                    <span className="ip-mark">{isSel ? "■" : "□"}</span>
                    <span className="ip-name">{i.name}</span>
                    {i.equivalent_ideas > 1 && (
                      <span className="ip-dup"> &times;{i.equivalent_ideas}</span>
                    )}
                    {isSel && hidden > 0 && (
                      <span className="ip-note"> &middot; {t("ideas.beforeChart", { n: hidden })}</span>
                    )}
                  </td>
                  <td className="ip-l"><span className="ip-cat">{i.category.replace(/_/g, " ")}</span></td>
                  <td className="ip-secondary">{tradeCount(i)}</td>
                  <td className={`ip-total ${neg ? "ip-neg-v" : "ip-pos-v"}`}>{fmtR(i.R)}</td>
                  <td className={i.R_per_trade > 0 ? "ip-pos-v" : "ip-neg-v"}>{fmtR(i.R_per_trade)}</td>
                  <td className="ip-secondary">{Number.isFinite(i.win_rate) ? `${i.win_rate.toFixed(0)}%` : "—"}</td>
                  <td className={`ip-ctrl${i.ctrl_pct >= 95 ? " ip-strong" : ""}`}>
                    {i.ctrl_pct == null ? "—" : ordinal(i.ctrl_pct)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="ip-foot">
        {t("ideas.foot", { profitable: s.profitable, tested: s.strategies_tested, total: fmtR(s.total_R) })}
      </p>
    </div>
  );
}
